// src/features/logs/pages/LogsPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../shared/api/apiClient";
import "./LogsPage.css";

const formatBackendError = (raw, fallback) => {
  if (!raw) return fallback;
  const detail = raw?.response?.data?.detail ?? raw;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    // pydantic style: [{type, loc, msg, input}, ...]
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  }

  if (typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }

  try {
    return String(detail);
  } catch {
    return fallback;
  }
};

// ===== helper для сортировки =====
const applySorting = (items, config) => {
  if (!config || !config.key) return items;

  const { key, direction } = config;
  const dir = direction === "asc" ? 1 : -1;

  const getVal = (row) => {
    if (!row) return null;

    switch (key) {
      case "id":
      case "emploee_id":
      case "user_id":
        return row[key] != null ? Number(row[key]) : null;
      case "created_at":
        return row[key] ? new Date(row[key]).getTime() : null;
      default:
        return row[key] ?? null;
    }
  };

  const sorted = [...items].sort((a, b) => {
    const av = getVal(a);
    const bv = getVal(b);

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });

  return sorted;
};

// дефолтные фильтры по колонкам
const defaultColumnFilters = {
  id: "",
  action: "",
  description: "",
  entity: "",
  emploee_id: "",
  user_id: "",
  created_at: "",
};

const LogsPage = () => {
  const { t } = useTranslation();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ----- сортировка (по умолчанию id desc) -----
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });

  // -------- фильтры / пагинация / saved filters --------
  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState(defaultColumnFilters);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState(null);
  const [savingFilter, setSavingFilter] = useState(false);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get("/auth/activityLog/all", {
        params: { skip: 0, limit: 100 },
      });

      setLogs(res.data || []);
      setCurrentPage(1);
    } catch (e) {
      console.error(e);
      setError(
        formatBackendError(
          e,
          t("error_fetching_logs")
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------- API: saved filters (activity_logs) ----------
  const loadSavedFilters = async () => {
    try {
      setFiltersLoading(true);
      setFiltersError(null);

      // имя сущности можешь поправить под свой бэк
      const res = await api.get("/saved-filters/activity_logs");
      const items = res.data || [];
      setSavedFilters(items);

      const def = items.find((f) => f.is_default);
      if (def && !selectedFilterId) {
        applySavedFilter(def);
      }
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail ||
          t("error_loading_saved_filters")
      );
    } finally {
      setFiltersLoading(false);
    }
  };

  const applySavedFilter = (filter) => {
    if (!filter) return;

    const filtersObj = filter.filters || {};
    const cf = filtersObj.columnFilters || defaultColumnFilters;
    const sq = filtersObj.searchQuery || "";

    setSearchQuery(sq);
    setColumnFilters({
      ...defaultColumnFilters,
      ...cf,
    });

    setSortConfig({
      key: filter.sort_by || "id",
      direction: filter.sort_dir || "desc",
    });

    setRowsPerPage(filter.per_page || 10);
    setCurrentPage(1);
    setSelectedFilterId(filter.id);
  };

  const handleSelectSavedFilter = (e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedFilterId(null);
      return;
    }

    const id = Number(val);
    const f = savedFilters.find((x) => x.id === id);
    if (f) {
      applySavedFilter(f);
    }
  };

  const handleSaveCurrentAsNew = async () => {
    const name =
      window.prompt(t("enter_filter_name")) || "";
    if (!name.trim()) return;

    const makeDefault = window.confirm(
      t("make_filter_default")
    );

    const payload = {
      name: name.trim(),
      filters: {
        searchQuery,
        columnFilters,
      },
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
      per_page: rowsPerPage,
      is_default: makeDefault,
    };

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.post("/saved-filters/activity_logs", payload);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail ||
          t("error_saving_filter")
      );
    } finally {
      setSavingFilter(false);
    }
  };

  const handleUpdateSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(
        t("select_filter_first")
      );
      return;
    }

    const makeDefault = window.confirm(
      t("make_filter_default")
    );

    const payload = {
      name: undefined,
      filters: {
        searchQuery,
        columnFilters,
      },
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
      per_page: rowsPerPage,
      is_default: makeDefault,
    };

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.put(`/saved-filters/${selectedFilterId}`, payload);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail ||
          t("error_updating_filter")
      );
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(
        t("select_filter_first")
      );
      return;
    }

    const confirmed = window.confirm(
      t("confirm_delete_filter")
    );
    if (!confirmed) return;

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.delete(`/saved-filters/${selectedFilterId}`);
      setSelectedFilterId(null);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail ||
          t("error_deleting_filter")
      );
    } finally {
      setSavingFilter(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    loadSavedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ======== ПОИСК / ФИЛЬТРЫ / СОРТ / ПАГИНАЦИЯ ========

  const normalize = (v) =>
    v === null || v === undefined ? "" : String(v).toLowerCase();

  const formatDateForFilter = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleString().toLowerCase();
    } catch {
      return String(value).toLowerCase();
    }
  };

  const filteredAndSortedLogs = useMemo(() => {
    let result = [...logs];

    const global = searchQuery.trim().toLowerCase();

    // глобальный поиск (если когда-нибудь вернёшь инпут – всё уже готово)
    if (global) {
      result = result.filter((row) => {
        const fields = [
          row.id,
          row.action,
          row.description,
          row.entity,
          row.emploee_id,
          row.user_id,
          formatDateForFilter(row.created_at),
        ];
        return fields.some((f) => normalize(f).includes(global));
      });
    }

    // фильтры по колонкам
    result = result.filter((row) => {
      const cf = columnFilters;

      if (cf.id && !String(row.id ?? "").includes(cf.id.trim())) return false;
      if (
        cf.action &&
        !normalize(row.action).includes(cf.action.trim().toLowerCase())
      )
        return false;
      if (
        cf.description &&
        !normalize(row.description).includes(
          cf.description.trim().toLowerCase()
        )
      )
        return false;
      if (
        cf.entity &&
        !normalize(row.entity).includes(cf.entity.trim().toLowerCase())
      )
        return false;
      if (
        cf.emploee_id &&
        !String(row.emploee_id ?? "").includes(cf.emploee_id.trim())
      )
        return false;
      if (
        cf.user_id &&
        !String(row.user_id ?? "").includes(cf.user_id.trim())
      )
        return false;
      if (
        cf.created_at &&
        !formatDateForFilter(row.created_at).includes(
          cf.created_at.trim().toLowerCase()
        )
      )
        return false;

      return true;
    });

    // сортировка
    result = applySorting(result, sortConfig);

    return result;
  }, [logs, searchQuery, columnFilters, sortConfig]);

  // пагинация
  const totalRows = filteredAndSortedLogs.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / (rowsPerPage || 1)));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * rowsPerPage;
  const visibleRows = filteredAndSortedLogs.slice(
    startIdx,
    startIdx + rowsPerPage
  );

  const paginate = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    const value = Number(e.target.value);
    if (!value || value <= 0) return;
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1);
  };

  // сортировка по клику по заголовку
  const handleSort = (key) => {
    setSortConfig((prev) => {
      let direction = "asc";
      if (prev.key === key && prev.direction === "asc") {
        direction = "desc";
      }
      return { key, direction };
    });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const showingText =
    totalRows === 0
      ? t("showing_entries", { start: 0, end: 0, total: 0 })
      : t("showing_entries", {
          start: startIdx + 1,
          end: Math.min(startIdx + rowsPerPage, totalRows),
          total: totalRows,
        });

  // refresh в стиле Leads/Deals
  const handleRefreshClick = async () => {
    setSearchQuery("");
    setColumnFilters(defaultColumnFilters);
    setSortConfig({ key: "id", direction: "desc" });
    setRowsPerPage(10);
    setCurrentPage(1);
    setSelectedFilterId(null);
    setFiltersError(null);
    await fetchLogs();
  };

  return (
    <div className="container mt-4 logsPage">
      {/* верхний хедер: заголовок + Refresh (как в Leads) */}
      <div className="logsPageHeader d-flex justify-content-between align-items-center mb-3 gap-2 flex-nowrap">
        <h2 className="mb-0">{t("activity_logs_title")}</h2>
        <div className="d-flex gap-2 align-items-center flex-nowrap">
          <button
            className="btn btn-outline-secondary"
            onClick={handleRefreshClick}
            disabled={loading}
          >
            {loading
              ? `${t("loading")}...`
              : t("refresh")}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2">
          {typeof error === "string" ? error : formatBackendError(error, "")}
        </div>
      )}
      {filtersError && (
        <div className="alert alert-warning py-2">{filtersError}</div>
      )}

      <div className="table-responsive logsDesktopTable">
        {/* верхняя линия над таблицей: per-page + Showing + Saved filters */}
        <div className="logsDesktopToolbar d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 px-2 pt-2">
          {/* слева: select per-page + текст */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
              className="form-select form-select-sm"
              style={{ width: 90 }}
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>

            <div className="text-muted">{showingText}</div>
          </div>

          {/* справа: Saved filters + иконки, как в Leads/Deals */}
          <div
            className="input-group input-group-sm"
            style={{ width: "auto", minWidth: 260 }}
          >
            <select
              className="form-select"
              value={selectedFilterId || ""}
              onChange={handleSelectSavedFilter}
              disabled={filtersLoading}
            >
              <option value="">
                {filtersLoading
                  ? `${t("loading")}...`
                  : t("saved_filters")}
              </option>
              {savedFilters.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.is_default ? " ⭐" : ""}
                </option>
              ))}
            </select>

            <button
              className="btn btn-outline-success"
              type="button"
              title={t("save_filter")}
              onClick={handleSaveCurrentAsNew}
              disabled={savingFilter}
            >
              <i className="lni lni-floppy-disk-1"></i>
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              title={t("update_filter")}
              onClick={handleUpdateSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-refresh-circle-1-clockwise"></i>
            </button>

            <button
              className="btn btn-outline-danger"
              type="button"
              title={t("delete_filter")}
              onClick={handleDeleteSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-trash-3"></i>
            </button>
          </div>
        </div>

        <table className="table table-striped table-hover align-middle">
          <thead>
            <tr>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("id")}>
                {t("log_id", { defaultValue: "ID" })}
                {renderSortArrow("id")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("action")}
              >
                {t("log_action", { defaultValue: "Action" })}
                {renderSortArrow("action")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("description")}
              >
                {t("log_description")}
                {renderSortArrow("description")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("entity")}
              >
                {t("log_entity")}
                {renderSortArrow("entity")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("emploee_id")}
              >
                {t("log_emploee") || t("log_employee")}
                {renderSortArrow("emploee_id")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("user_id")}
              >
                {t("log_user")}
                {renderSortArrow("user_id")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("created_at")}
              >
                {t("log_created_at")}
                {renderSortArrow("created_at")}
              </th>
            </tr>

            {/* строка с фильтрами по колонкам */}
            <tr>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("filter_id", { defaultValue: "ID" })}
                  value={columnFilters.id}
                  onChange={(e) =>
                    handleColumnFilterChange("id", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("filter_action", { defaultValue: "Action" })}
                  value={columnFilters.action}
                  onChange={(e) =>
                    handleColumnFilterChange("action", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("filter_description") || t("log_description")}
                  value={columnFilters.description}
                  onChange={(e) =>
                    handleColumnFilterChange("description", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("filter_entity") || t("log_entity")}
                  value={columnFilters.entity}
                  onChange={(e) =>
                    handleColumnFilterChange("entity", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("filter_emploee") || t("filter_employee") || t("log_emploee") || t("log_employee")}
                  value={columnFilters.emploee_id}
                  onChange={(e) =>
                    handleColumnFilterChange("emploee_id", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("filter_user") || t("log_user")}
                  value={columnFilters.user_id}
                  onChange={(e) =>
                    handleColumnFilterChange("user_id", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("filter_created_at") || t("log_created_at")}
                  value={columnFilters.created_at}
                  onChange={(e) =>
                    handleColumnFilterChange("created_at", e.target.value)
                  }
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="text-center py-4">
                  {t("activity_logs_empty")}
                </td>
              </tr>
            )}

            {visibleRows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{row.action}</td>
                <td>{row.description || t("n_a")}</td>
                <td>{row.entity}</td>
                <td>{row.emploee_id}</td>
                <td>{row.user_id ?? t("n_a")}</td>
                <td>
                  {row.created_at
                    ? new Date(row.created_at).toLocaleString()
                    : t("n_a")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="logsMobileTableCard">
        <div className="logsMobileTableToolbar">
          <div className="logsMobileToolbarTitle">
            {t("table_filters", { defaultValue: "Table filters" })}
          </div>

          <div className="logsMobileToolbarActions">
            <select
              className="form-select"
              value={selectedFilterId || ""}
              onChange={handleSelectSavedFilter}
              disabled={filtersLoading}
            >
              <option value="">
                {filtersLoading
                  ? `${t("loading")}...`
                  : t("saved_filters")}
              </option>
              {savedFilters.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.is_default ? " ⭐" : ""}
                </option>
              ))}
            </select>

            <button
              className="btn btn-outline-success"
              type="button"
              title={t("save_filter")}
              onClick={handleSaveCurrentAsNew}
              disabled={savingFilter}
            >
              <i className="lni lni-floppy-disk-1"></i>
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              title={t("update_filter")}
              onClick={handleUpdateSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-refresh-circle-1-clockwise"></i>
            </button>

            <button
              className="btn btn-outline-danger"
              type="button"
              title={t("delete_filter")}
              onClick={handleDeleteSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-trash-3"></i>
            </button>
          </div>
        </div>

        <div className="logsMobileTableMeta">
          <select
            className="form-select"
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>

          <div>{showingText}</div>
        </div>

        <div className="logsMobileTableSearch">
          <input
            type="text"
            className="form-control"
            placeholder={t("search", { defaultValue: "Search" })}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="logsMobileTableScroll">
          <table className="logsMobileTable">
            <thead>
              <tr>
                <th>{t("log_id", { defaultValue: "ID" })}</th>
                <th>{t("log_action", { defaultValue: "Action" })}</th>
              </tr>

              <tr>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("filter_id", { defaultValue: "ID" })}
                    value={columnFilters.id}
                    onChange={(e) =>
                      handleColumnFilterChange("id", e.target.value)
                    }
                  />
                </th>

                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("filter_action", { defaultValue: "Action" })}
                    value={columnFilters.action}
                    onChange={(e) =>
                      handleColumnFilterChange("action", e.target.value)
                    }
                  />
                </th>
              </tr>
            </thead>

            <tbody>
              {visibleRows.length === 0 && !loading && (
                <tr>
                  <td colSpan={2} className="logsMobileEmptyCell">
                    {t("activity_logs_empty")}
                  </td>
                </tr>
              )}

              {visibleRows.map((row) => (
                <tr key={row.id}>
                  <td className="logsMobileIdCell">{row.id}</td>

                  <td>
                    <div className="logsMobileLogCell">
                      <div className="logsMobileLogTop">
                        <strong>{row.action}</strong>
                        <span className="logsMobileEntity">
                          {row.entity || t("n_a")}
                        </span>
                      </div>

                      <div className="logsMobileLogMeta">
                        <span>
                          {t("log_description")}:{" "}
                          <b>{row.description || t("n_a")}</b>
                        </span>

                        <span>
                          {t("log_emploee") || t("log_employee")}:{" "}
                          <b>{row.emploee_id ?? t("n_a")}</b>
                        </span>

                        <span>
                          {t("log_user")}:{" "}
                          <b>{row.user_id ?? t("n_a")}</b>
                        </span>

                        <span>
                          {t("log_created_at")}:{" "}
                          <b>
                            {row.created_at
                              ? new Date(row.created_at).toLocaleString()
                              : t("n_a")}
                          </b>
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={2} className="logsMobileEmptyCell">
                    {t("loading")}...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* пагинация снизу, справа */}
      <div className="d-flex justify-content-end mt-2 logsPaginationWrap">
        <nav aria-label={t("activity_logs_pagination_aria")}>
          <ul className="pagination justify-content-end mb-0 logsPagination">
            <li
              className={`page-item ${
                safePage === 1 || totalRows === 0 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                title={t("previous")}
                aria-label={t("previous")}
                onClick={() => paginate(safePage - 1)}
              >
                <i className="lni lni-arrow-left"></i>
              </button>
            </li>
            {Array.from({ length: totalPages }, (_, idx) => (
              <li
                key={idx}
                className={`page-item ${
                  safePage === idx + 1 ? "active" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => paginate(idx + 1)}
                >
                  {idx + 1}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                safePage === totalPages || totalRows === 0 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                title={t("next")}
                aria-label={t("next")}
                onClick={() => paginate(safePage + 1)}
              >
                <i className="lni lni-arrow-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default LogsPage;